// Plays the sounds of the moments that a store shows: the stage, the history
// cursor, the turn, a piece the AI lifts, and a press on a button. The die,
// the wheel, the board, the menu, and a tap on a square play their own, at the
// frame they change. A sound is presentation. The game never waits for one.

import {
  OWN_SOUND_ATTRIBUTE,
  SOUND_CARD_AFTER_LAND_MS,
  SOUND_POLARITY_AFTER_LAND_MS,
} from '../constants';
import type { GameState } from '../game/state';
import { playSound, startSoundPlayer } from '../sound/player';
import { spellById } from '../spells/library';
import { carryStore } from './carry';
import { whenQuiet } from './director';
import { gameStore } from './game';
import { type HistoryState, historyStore, isReviewing } from './history';
import { actsFor, localSide } from './localSide';
import { type StagePhase, type StageState, stageStore } from './stage';

/** The place of the cursor as a number. The live game is one past the last step. */
const cursorPlace = ({ steps, cursor }: HistoryState): number => cursor ?? steps.length;

const startStageSounds = (): void => {
  let phase: StagePhase = stageStore.get().phase;
  stageStore.subscribe((stage: StageState) => {
    const before = phase;
    phase = stage.phase;
    if (phase === before || isReviewing()) return;
    // A roll for energy shows no card, so only the die sounds.
    const spellId = stage.roll?.spellId ?? null;
    if (phase === 'landed' && stage.roll) {
      playSound('dieLand');
      if (spellId !== null) {
        playSound('cardUp', SOUND_CARD_AFTER_LAND_MS);
        playSound(spellById(spellId).polarity, SOUND_POLARITY_AFTER_LAND_MS);
      }
    } else if (phase === 'leaving' && spellId !== null) playSound('cardDown');
    else if (phase === 'over') {
      // A match that nobody won has no sound.
      const { winner } = gameStore.get();
      if (winner !== null) playSound(winner === localSide() ? 'win' : 'lose');
    }
  });
};

const startHistorySounds = (): void => {
  let place = cursorPlace(historyStore.get());
  let reviewing = isReviewing();
  historyStore.subscribe((history) => {
    const before = place;
    const wasReviewing = reviewing;
    place = cursorPlace(history);
    reviewing = history.cursor !== null;
    // A new step of the live game moves the place and is no step of the player.
    if (!wasReviewing && !reviewing) return;
    if (place < before) playSound('back');
    else if (place > before) playSound('forward');
  });
};

const startTurnSounds = (): void => {
  let last: GameState = gameStore.get();
  /** Counts the turns that came to the player, so an old wait does not sound. */
  let asked = 0;
  gameStore.subscribe((state) => {
    const before = last;
    last = state;
    const cameToPlayer =
      state.seed === before.seed &&
      before.turn !== localSide() &&
      actsFor(state.turn) &&
      state.phase !== 'over';
    if (!cameToPlayer) return;
    asked += 1;
    const mine = asked;
    void whenQuiet().then(() => {
      const now = gameStore.get();
      if (mine === asked && actsFor(now.turn) && now.phase !== 'over') playSound('yourTurn');
    });
  });
};

const startCarrySounds = (): void => {
  let lifted = carryStore.get() !== null;
  carryStore.subscribe((carry) => {
    if (carry !== null && !lifted) playSound('lift');
    lifted = carry !== null;
  });
};

const startPressSounds = (): void => {
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button');
    if (button && !button.disabled && !button.hasAttribute(OWN_SOUND_ATTRIBUTE)) playSound('press');
  });
};

/** Call once at startup. */
export const startSoundDriver = (): void => {
  startSoundPlayer();
  startStageSounds();
  startHistorySounds();
  startTurnSounds();
  startCarrySounds();
  startPressSounds();
};
